import type { settings as settingsEn } from "../en/settings";

export const settings: typeof settingsEn = {
  badges: {
    configured: "Nastaveno",
    notSet: "Nenastaveno",
    savedTypeToReplace: "•••••••••• (uloženo — pište pro nahrazení)",
  },
  page: {
    title: "Nastavení",
    subtitle: "Správa nastavení webu a systému",
    loadFailed: "Nepodařilo se načíst nastavení",
    loadFailedRefresh: "Nepodařilo se načíst nastavení. Obnovte prosím stránku.",
    saved: "Nastavení uloženo",
    saveFailed: "Nepodařilo se uložit nastavení",
    unsavedConfirm: "Máte neuložené změny. Pokračovat bez uložení?",
    unsavedChanges: "Máte neuložené změny",
    saveChanges: "Uložit změny",
    tabsAriaLabel: "Záložky",
    tabs: {
      company: "Společnost",
      smtp: "SMTP",
      notifications: "Notifikace",
      booking: "Rezervace",
      seo: "SEO a analytika",
      social: "Sociální sítě",
      security: "Zabezpečení",
      ai: "AI (ChatGPT)",
      marketing: "Marketing a měření",
      content: "Obsah webu",
      finance: "Finance",
      cache: "Cache",
    },
  },
  company: {
    title: "Údaje o společnosti",
    companyName: "Název společnosti",
    businessId: "IČO",
    taxId: "DIČ",
    licenseNumber: "Číslo koncese cestovní kanceláře",
    licenseNumberPlaceholder: "Např. 123456/2020",
    address: "Adresa sídla",
    contactEmail: "Kontaktní e-mail",
    phone: "Telefon",
    bankDetailsTitle: "Bankovní údaje",
    accountNumber: "Číslo účtu",
    iban: "IBAN",
    swift: "SWIFT/BIC",
    bankName: "Název banky",
    googleReviewsTitle: "Recenze Google",
    rating: "Hodnocení",
    reviewCount: "Počet recenzí",
    reviewUrl: "URL recenzí",
    distributionTitle: "Rozložení hodnocení",
    distributionHint:
      "Pět čísel opsaných z profilu na Googlu. Graf se na webu zobrazí, jen když je vyplněných všech pět; nula je platná hodnota.",
    stars5: "5 hvězdiček",
    stars4: "4 hvězdičky",
    stars3: "3 hvězdičky",
    stars2: "2 hvězdičky",
    stars1: "1 hvězdička",
    distributionSum: "Součet rozložení",
    distributionSumIncomplete: "nevyplněno",
    distributionEnteredTotal: "zadaný celkový počet",
    googleManualFallbackHint:
      "Tato čísla platí na webu jen do prvního nahraného exportu Takeout (Google recenze → Nahrát export). Potom má přednost souhrn z exportu a tato čísla zůstávají jako záloha.",
  },
  smtp: {
    bulkInfoTitle: "Transport hromadného newsletteru.",
    bulkInfoBody:
      "Transakční e-maily (potvrzení objednávek a poptávek) se odesílají přes připojenou schránku Gmail — toto SMTP slouží pro hromadný newsletter. Nejprve nastavení uložte, poté odešlete test. Tip: pro dobrou doručitelnost použijte odesílatele na doméně nakonfigurované u vašeho SMTP hostingu (SPF/DKIM).",
    serverTitle: "SMTP server",
    host: "SMTP host",
    port: "Port",
    port587: "587 (STARTTLS – doporučeno)",
    port465: "465 (SSL)",
    port25: "25 (nešifrované – na Cloudflare blokováno)",
    port2525: "2525 (alternativní)",
    username: "Uživatelské jméno",
    password: "Heslo",
    storedEncrypted: "Uloženo šifrovaně; po uložení se už nikdy nezobrazí.",
    encryption: "Šifrování",
    encryptionTls: "TLS",
    encryptionSsl: "SSL",
    encryptionNone: "Žádné",
    senderTitle: "Odesílatel",
    senderName: "Jméno odesílatele",
    senderEmail: "E-mail odesílatele (From)",
    replyTo: "E-mail pro odpovědi (Reply-To)",
    sendTest: "Odeslat testovací e-mail",
    sending: "Odesílám…",
    sendTestHint: "Odešle se na e-mail vašeho účtu přes uložené SMTP.",
    serverConnectionError: "Chyba spojení se serverem",
  },
  notifications: {
    title: "E-mailové notifikace",
    items: {
      newOrder: "Nová objednávka",
      newOrderDesc: "Když je vytvořena nová objednávka",
      orderPaid: "Přijatá platba",
      orderPaidDesc: "Když zákazník zaplatí",
      orderCancelled: "Zrušená objednávka",
      orderCancelledDesc: "Když je objednávka zrušena",
      newInquiry: "Nová poptávka",
      newInquiryDesc: "Když dorazí nezávazná poptávka",
      newReview: "Nová recenze",
      newReviewDesc: "Když zákazník napíše recenzi",
      lowCapacity: "Nízká kapacita",
      lowCapacityDesc: "Když zbývají méně než 3 volná místa",
    },
    recipientsTitle: "Příjemci notifikací",
    recipientsLabel: "E-maily (oddělené čárkou)",
    slackTitle: "Integrace Slack",
    webhookUrl: "Webhook URL",
  },
  booking: {
    title: "Nastavení rezervací",
    depositPercent: "Výše zálohy (%)",
    depositDueDays: "Splatnost zálohy (dny)",
    finalPaymentDaysBefore: "Doplatek (dny před odjezdem)",
    minParticipants: "Min. počet účastníků",
    maxParticipants: "Max. počet účastníků",
    cancellationTitle: "Storno podmínky",
    daysRange: "{{range}} dní",
    percentOfPrice: "% z ceny",
  },
  seo: {
    metaTitle: "Meta tagy (výchozí)",
    siteTitle: "Titulek webu",
    metaDescription: "Popis (meta description)",
    keywords: "Klíčová slova",
    verificationTitle: "Search Console / ověření webu",
    verificationHint:
      "Vložte ověřovací token (jen hodnotu content, ne celý meta tag) — vykreslí se do <head> každé stránky. Aktivní GA4 measurement ID se nastavuje na záložce Marketing.",
    googleSearchConsole: "Google Search Console",
    googleSearchConsolePlaceholder: "token google-site-verification",
    bingWebmaster: "Bing Webmaster (volitelné)",
    bingWebmasterPlaceholder: "token msvalidate.01",
    pinterest: "Pinterest (volitelné)",
    pinterestPlaceholder: "token p:domain_verify",
    googleApiTitle: "Google API (pro analytický dashboard)",
    infoTitle: "Info:",
    infoBody:
      "Tyto přístupové údaje slouží k načítání dat z Google Analytics 4 a Google Ads přímo do dashboardu v adminu. Vyžaduje Service Account s přístupem ke GA4 property a účtu Google Ads.",
    ga4PropertyId: "GA4 Property ID",
    ga4PropertyIdHint: "Najdete v GA4 > Admin > Property Settings",
    googleAdsCustomerId: "Google Ads Customer ID",
    googleAdsCustomerIdHint: "Najdete v Google Ads > horní menu (formát: XXX-XXX-XXXX)",
    googleAdsDeveloperToken: "Google Ads Developer Token",
    googleAdsDeveloperTokenHint: "Získáte v Google Ads > Tools > API Center (vyžaduje schválení)",
    serviceAccountJson: "Google Service Account JSON",
    serviceAccountJsonHint:
      "JSON klíč ze Service Accountu v Google Cloud Console. Service Account musí mít přístup ke GA4 property (Admin > Property Access Management) a k účtu Google Ads (Admin > Linked accounts).",
  },
  social: {
    title: "Sociální sítě",
    podcastSync: {
      title: "Podcasty (synchronizace z YouTube)",
      description:
        "Volitelná nadstavba. Epizody jde spravovat ručně i bez těchto údajů — bez ID playlistu nebo bez klíče se jen nic nesynchronizuje.",
      playlistIdLabel: "ID YouTube playlistu",
      apiKeyLabel: "API klíč (YouTube Data API v3)",
      apiKeyHint:
        "Klíč se ukládá zašifrovaně a nikdy se nezobrazí. Prázdné pole klíč NEMAŽE — jen ho nemění.",
      syncNow: "Synchronizovat nyní",
      notConfigured:
        "Synchronizace není nastavená (chybí ID playlistu nebo API klíč) — nic se nezměnilo.",
      syncDone:
        "Hotovo: {{fetched}} epizod z playlistu, {{created}} nových, {{updated}} upravených.",
    },
  },
  security: {
    title: "Zabezpečení",
    autoLogout: "Automatické odhlášení po (minuty)",
    minutes30: "30 minut",
    hour1: "1 hodina",
    hours2: "2 hodiny",
    hours8: "8 hodin",
    hours24: "24 hodin",
    items: {
      require2fa: "Dvoufaktorové ověření (2FA)",
      require2faDesc: "Vyžadovat 2FA u všech administrátorů",
      logAccess: "Logování přístupů",
      logAccessDesc: "Zaznamenávat všechna přihlášení a akce administrátorů",
      emailOnNewLogin: "E-mail při novém přihlášení",
      emailOnNewLoginDesc: "Odeslat e-mail při přihlášení z nového zařízení",
    },
    passwordPolicyTitle: "Pravidla pro hesla",
    minPasswordLength: "Minimální délka hesla",
    passwordExpiration: "Platnost hesla (dny, 0 = neomezeně)",
  },
  ai: {
    test: "Otestovat",
    connectionWorks: "Připojení k {{provider}} funguje",
    connectFailed: "Nepodařilo se připojit k {{provider}}",
    anthropicTitle: "Anthropic / Claude — agent „MAKEJ!“",
    anthropicDesc:
      "Pohání AI provozního asistenta (chat + kontextové akce napříč adminem). Bez klíče zůstává agent nedostupný; vše ostatní funguje dál.",
    apiKeyLabel: "Anthropic API klíč",
    apiKeyHelp:
      "API klíč získáte na <0>console.anthropic.com</0>. Uloženo šifrovaně — po uložení se už nikdy nezobrazí.",
    featuresTitle: "Funkce",
    segmentationLabel: "AI segmentace zákazníků",
    segmentationDesc:
      "Umožňuje vytvářet segmenty zákazníků přirozeným jazykem (např. „zákazníci, kteří loni navštívili Bali“)",
    personalizationLabel: "AI personalizace e-mailů",
    personalizationDesc:
      "V automatických kampaních automaticky přizpůsobuje obsah e-mailů profilu zákazníka",
    fourEyesLabel: "Kontrola čtyř očí pro peněžní akce MAKEJ!",
    fourEyesDesc:
      "Storna, změny termínů, faktury a e-maily navržené agentem musí schválit druhý operátor — kdo návrh podal, nemůže ho sám schválit. Pokud admin obsluhuje jediný člověk, nechte vypnuté.",
    brandToneTitle: "Značka a tón MAKEJ!",
    brandToneIntro:
      "Nalaďte, jak agent <0>zní</0> — hlas, jazyk a podpis — v chatu i kontextových tlačítkách. Jde <1>pouze o tón</1>: nikdy nemění, co agent smí dělat. Pravidla ukotvení a čtyř očí mají vždy přednost, takže agent dál odmítne uvést cenu, kterou nedokáže ověřit, nebo cokoli odeslat bez schválení.",
    languageLabel: "Jazyk",
    languagePlaceholder: "čeština",
    languageHint: "Jazyk, kterým MAKEJ! píše (např. angličtina, čeština, vietnamština).",
    signOffLabel: "Podpis",
    signOffPlaceholder: "— Tým CK Go2",
    signOffHint: "Jak se koncepty podepisují (volitelné).",
    brandVoiceLabel: "Hlas značky",
    brandVoicePlaceholder:
      "Vřelý, znalý a v klidu — jako scestovalý kamarád. Sebejistý, ale nikdy vlezlý.",
    brandVoiceHint: "Krátký popis tónu, který chcete v konceptech pro zákazníky.",
    houseRulesLabel: "Domácí pravidla",
    houseRulesPlaceholder:
      "Jen preference formulací — např. vždy zdravit křestním jménem, nepoužívat vykřičníky, říkat „zájezd“ místo „tour“, nabízet odkaz na zákaznický portál, když se hodí.",
    houseRulesHint:
      "Doplňkové pokyny k formulacím a tónu. Nemohou přebít bezpečnostní pravidla — agent si dál nebude vymýšlet fakta ani jednat bez schválení.",
    agentEmailTitle: "Agent přes e-mail",
    agentEmailIntro:
      "Umožněte zaměstnanci napsat agentovi e-mail (se zmínkou spouštěcího tokenu) a dostat odpověď — bez otevírání adminu. Agent odpovídá na otázky a připravuje koncepty, ale <0>nikdy nic nevykonává</0>: každou změnu musí stále schválit přihlášená osoba v aplikaci. Odpovědi chodí vždy na e-mail účtu daného zaměstnance.",
    emailChannelLabel: "Zapnout e-mailový kanál",
    emailChannelDesc:
      "Ve výchozím stavu vypnuto. Po zapnutí agent každých 5 minut prochází nově synchronizovanou příchozí poštu, hledá spouštěcí token od známého odesílatele z týmu a odpovídá ve vlákně. Vyžaduje připojenou schránku Gmail.",
    triggerTokenLabel: "Spouštěcí token",
    triggerTokenDesc:
      "Zmiňte ho kdekoli v předmětu nebo těle e-mailu a agent se aktivuje (např. <0>@makej kolik lidí je na odjezdu GO2-…?</0>).",
    readOnlyLabel: "Režim jen pro čtení (pouze otázky)",
    readOnlyDesc:
      "Nejkonzervativnější nastavení: přes e-mail agent pouze odpovídá na otázky a nikdy nepřipravuje návrhy. Nechte vypnuté, pokud má připravovat i koncepty (které stále vyžadují schválení v aplikaci).",
    requireAuthLabel: "Vyžadovat ověřeného odesílatele (doporučeno)",
    requireAuthDesc:
      "Reagovat jen na poštu, která projde e-mailovou autentizací (DKIM zarovnaný s doménou odesílatele, nebo DMARC) — podvržený e-mail „z adresy zaměstnance“ je tak ignorován. Nechávejte zapnuté, ledaže tým odesílá z domény bez DKIM/DMARC.",
    triageTitle: "Třídění příchozí pošty",
    triageIntro:
      "Každou novou zprávu z kontaktního formuláře i nevyžádaný e-mail klasifikuje podle <0>záměru</0> — prodej / dotaz / dodavatel / šum. Každá položka dostane v inboxu štítek záměru pro třídění jedním kliknutím. Když je zapnuté automatické vytváření, <1>jistý prodejní</1> dotaz se automaticky promění v poptávku (s AI konceptem první odpovědi připraveným ke kontrole).",
    triageAutoCreateLabel: "Automaticky vytvářet poptávky z jistých prodejních dotazů",
    triageAutoCreateDesc:
      "Ve výchozím stavu vypnuto (navrhnout → schválit): položky dostanou jen štítek záměru, se kterým naložíte sami. Zapněte, až budete klasifikaci důvěřovat — jasné prodejní dotazy se pak stanou poptávkami samy; dotazy, dodavatelé a spam se nikdy nekonvertují automaticky.",
    confidenceLabel: "Práh jistoty pro automatické vytvoření",
    confidenceDesc:
      "Poptávka se automaticky vytvoří, jen když si je model alespoň takto jistý, že jde o prodejní dotaz. Vyšší = konzervativnější (méně falešných poptávek). Platí jen při zapnutém automatickém vytváření.",
    leadRoutingTitle: "Automatické přidělování poptávek",
    leadRoutingIntro:
      "Nové poptávky se přidělují automaticky: nejprve <0>garant</0> odpovídající destinace, jinak aktivní obchodník s nejméně otevřenými poptávkami (round-robin podle vytížení). Ruční přeřazení je vždy možné. Jednotlivé destinace se mohou na své editační stránce odhlásit — jejich poptávky pak zůstávají nepřiřazené pro ruční třídění.",
    leadRoutingLabel: "Zapnout automatické přidělování poptávek",
    leadRoutingDesc:
      "Ve výchozím stavu vypnuto — nové poptávky zůstávají nepřiřazené, dokud si je někdo nevezme. Zapněte, chcete-li nahradit ruční pondělní třídění.",
    entryTitle: "Automatické podávání vstupních formulářů",
    entryIntro:
      "Jakmile se otevře okno pro podání, automaticky vyplňuje příletové karty na vládních portálech (cloudový prohlížeč) — po jednotlivých cestujících, jen se souhlasem klienta s portálem a jen pro země přepnuté ve znalostech zemí na kanál prohlížeče. QR/potvrzení se uloží ke vstupnímu úkolu a klient dostane e-mailem odkaz na portál. Nejprve spusťte <0>zkušební běh</0> z panelu vstupních dokumentů u termínu — vyplní celý formulář, zastaví se před odesláním a uloží screenshot ke kontrole.",
    entryLabel: "Zapnout autopilota vstupních formulářů",
    entryDesc:
      "Globální hlavní vypínač, ve výchozím stavu vypnuto. Tři po sobě jdoucí selhání v zemi ji automaticky přepnou zpět na ruční režim (ochrana před změnou portálu).",
    callsTitle: "Telefonní hovory",
    callsIntro:
      "Přepisy hovorů doručené e-mailem (např. z diktafonu Plaud) systém rozpozná, AI je shrne a uloží jako hovory na časovou osu zákazníka/poptávky — s navrženým follow-upem, který použijete jedním kliknutím. Jistý prodejní hovor z neznámého čísla se stane poptávkou podle stejných pravidel třídění jako e-mail.",
    transcriptSendersLabel: "Odesílatelé přepisů",
    transcriptSendersDesc:
      "Domény nebo adresy oddělené čárkou, jejichž e-maily se vždy považují za přepis hovoru. Prázdné = výchozí (<0>plaud.ai</0>). Pošta od jiných odesílatelů se rozpozná i tak, pokud předmět zmiňuje přepis a tělo vypadá jako dialog.",
    webhookSecretLabel: "Tajný klíč webhooku hovorů",
    webhookSecretDesc:
      "Pro budoucí cloudovou ústřednu (CloudTalk): přepisy hovorů posílejte POSTem na <0>/api/webhooks/calls</0> s touto hodnotou v hlavičce <1>X-Webhook-Secret</1>. Endpoint zůstává skrytý (404), dokud zde není uložen tajný klíč.",
    webhookSecretPlaceholder: "dlouhý náhodný řetězec",
  },
  marketing: {
    rotateConfirm:
      "Opravdu vyměnit klíč feedu? Surya přestane stahovat data, dokud nevložíte nový klíč.",
    generateKeyFailed: "Nepodařilo se vygenerovat klíč",
    backfillDone: "Doplnění hotovo — {{inserted}} nových z {{scanned}} zaplacených objednávek.",
    backfillFailed: "Doplnění selhalo",
    downloadFailed: "Stažení selhalo",
    consentTitle: "Souhlas s cookies",
    consentDesc:
      "Řídí, kdy se smí spouštět analytické a marketingové tagy. Naše vlastní first-party měření běží vždy (anonymně). Cookie lišta se zobrazuje na veřejném webu.",
    consentModeLabel: "Režim souhlasu",
    consentModeHint:
      "Geo: návštěvníci z EU/EHP/UK musí souhlas udělit (opt-in); všichni ostatní jsou opt-out.",
    consentGeo: "Geo — EU opt-in, zbytek opt-out (doporučeno)",
    consentOptIn: "Opt-in všude (přísné)",
    consentOptOut: "Opt-out všude",
    cookieBannerLabel: "Zobrazovat cookie lištu",
    cookieBannerDesc: "Zobrazit lištu souhlasu na veřejném webu.",
    freqCapTitle: "Frekvenční limit",
    freqCapDesc:
      "Pojistka napříč kanály, aby jeden kontakt nedostával příliš mnoho e-mailů. Každé marketingové odeslání — newsletter, automatické kampaně i journeys — ji před odesláním kontroluje; nikdy se nevztahuje na transakční poštu (potvrzení objednávek, faktury).",
    freqCapEnforceLabel: "Vynucovat frekvenční limit",
    freqCapEnforceDesc:
      "Při vypnutí jsou marketingové e-maily omezeny jen souhlasem (odhlášení z odběru platí i nadále).",
    maxPerWindowLabel: "Max. v okně",
    maxPerWindowHint:
      "Nejvyšší počet marketingových e-mailů, které jeden kontakt může dostat za okno.",
    windowDaysLabel: "Okno (dny)",
    windowDaysHint: "Klouzavé okno, přes které se limit počítá.",
    minGapLabel: "Min. rozestup (hodiny)",
    minGapHint: "Minimální čas mezi dvěma marketingovými e-maily. 0 = vypnuto.",
    googleTitle: "Google",
    googleDesc:
      "Analytika GA4 + měření konverzí Google Ads. Serverový upload (gclid) používá přístupové údaje Google Ads API ze záložky SEO a analytika.",
    ga4MeasurementIdLabel: "GA4 Measurement ID",
    ga4MeasurementIdHint: "např. G-XXXXXXXXXX",
    adsConversionIdLabel: "Google Ads Conversion ID",
    adsConversionIdHint: "např. AW-123456789",
    conversionLabelLabel: "Conversion Label",
    conversionLabelHint: "Label konverzní akce nákupu.",
    serverSideLabel: "Serverové konverze",
    serverSideDesc:
      "Nahrává zaplacené konverze přes Google Ads API (gclid). Vyžaduje přístupové údaje API v SEO a analytika + ID konverzní akce níže.",
    conversionActionIdLabel: "Conversion Action ID (serverové)",
    conversionActionIdHint:
      "Číselné ID konverzní akce Google Ads — pro upload gclid, ne AW-… výše.",
    metaTitle: "Meta (Facebook / Instagram)",
    metaDesc:
      "Pixel (prohlížeč) + Conversions API (server) pro spolehlivé konverze. CAPI token se ukládá šifrovaně a už se nikdy nezobrazí.",
    metaPixelIdLabel: "Meta Pixel ID",
    metaPixelIdHint: "Číselné ID pixelu.",
    testEventCodeLabel: "Test Event Code",
    testEventCodeHint: "Volitelné — pro testovací provoz v Meta Events Manageru.",
    capiTokenLabel: "Meta CAPI Access Token",
    capiTokenHint: "Token Conversions API z Meta Events Manageru. Uloženo šifrovaně.",
    feedTitle: "Konverzní feed (PPC)",
    feedDesc:
      "Read-only feed zaplacených objednávek — který zájezd se prodal + skutečné tržby a marže + click-id — pro vaši PPC platformu (Surya), aby její agenti optimalizovali na zisk. Útrata za reklamu a ROAS zůstávají v Suryi; feed neobsahuje žádné osobní údaje.",
    feedUrlLabel: "URL feedu (pull endpoint)",
    feedUrlHint:
      "Surya jej stahuje s API klíčem jako Bearer tokenem. Podporuje ?since=<cursor> pro inkrementální stahování a ?format=csv.",
    copyUrl: "Kopírovat URL",
    feedKeyLabel: "API klíč feedu",
    feedKeyHint:
      "Bearer token, kterým se Surya prokazuje. Generuje se na serveru, ukládá šifrovaně a zobrazí se jen jednou.",
    rotateKey: "Vyměnit klíč",
    generateKey: "Vygenerovat klíč",
    copyNow: "Zkopírujte si ho hned — znovu se už nezobrazí:",
    copyKey: "Kopírovat klíč",
    backfillButton: "Doplnit ze zaplacených objednávek",
    downloadCsv: "Stáhnout CSV",
  },
  finance: {
    loadFailed: "Nepodařilo se načíst finanční nastavení",
    numberingSaved: "Číslování pro {{scope}} uloženo",
    numberingSaveFailed: "Nepodařilo se uložit číslování",
    configSaved: "Finanční konfigurace uložena",
    configSaveFailed: "Nepodařilo se uložit finanční konfiguraci",
    scopeOrders: "Objednávky",
    scopeLeads: "Poptávky (leady)",
    configTitle: "Finanční konfigurace",
    configDesc:
      "Vykazovací měna, směnné kurzy (používají se pro přepočet cizích částek do CZK), DPH a doba uchování pasových údajů po odjezdu. Výše zálohy, platební podmínky a storno pásma jsou v záložce <0>Rezervace</0>; bankovní účet a IČO/DIČ v záložce <1>Společnost</1>.",
    defaultCurrency: "Výchozí měna",
    vatRate: "Sazba DPH (%)",
    paymentQr: "Platební QR (portál)",
    qrAuto: "Automaticky (EUR → EPC, CZK → SPAYD)",
    qrOff: "Vypnuto (jen bankovní údaje)",
    passportRetention: "Uchování pasů (dny)",
    eventRetention: "Uchování událostí (dny)",
    eventRetentionHint:
      "GDPR: surové behaviorální události se po uplynutí tohoto okna mažou (≈425 = 14 měsíců).",
    ratesTitle: "Směnné kurzy (1 jednotka → CZK)",
    addCurrency: "Přidat měnu",
    saveConfig: "Uložit konfiguraci",
    numberingTitle: "Číslování dokladů",
    numberingDesc:
      "Nastavte prefix a příští číslo každé číselné řady. Čítače se resetují každý rok (rok je součástí řady). Pro navázání na existující řadu použijte „Příští číslo“ — např. nastavte 100 a další objednávka bude <0>GO2-{{year}}-0100</0>.",
    prefix: "Prefix",
    nextNumber: "Příští číslo",
    preview: "Náhled",
  },
  cache: {
    infoTitle: "Informace o cache",
    infoDesc:
      "Web používá 5minutovou cache pro rychlejší načítání. Změny z adminu se projeví automaticky do 5 minut, nebo můžete cache vymazat ručně.",
    clearTitle: "Vymazat cache",
    clearDesc: "Vymaže všechna data v cache a vynutí načtení z databáze",
    clearing: "Mažu…",
    cleared: "Cache byla úspěšně vymazána",
    clearFailed: "Nepodařilo se vymazat cache",
    statsTitle: "Statistiky",
    ttlLabel: "Cache TTL",
    regionLabel: "Region",
    regionGlobal: "Globální",
  },
  content: {
    title: "Obsah webu",
    planningBlockTitle: "Varianty plánovacího bloku",
    planningBlockDescription:
      "Text, který stránka destinace ukáže místo výchozí věty, když destinace nemá žádný zájezd. Variantu si destinace vybírá v záložce Nabídka. Prostý text; prázdné = web použije výchozí znění.",
    variantLabel: "Varianta {{n}}",
    reviewOriginTitle: "Texty o původu recenzí",
    reviewOriginDescription:
      "Texty použije web u recenzí. Prázdné pole = výchozí znění (zobrazené šedě).",
    reviewOriginLabels: {
      reviewsIntro: "Úvod",
      reviewsOriginClients: "Od klientů",
      reviewsOriginGoogle: "Na Googlu",
      reviewsOriginElsewhere: "Jinde na webu",
    },
    placeholdersTitle: "Zástupné hodnoty, které můžete v textech použít",
    placeholders: {
      score: "známka na Googlu (např. 4,9)",
      count: "počet recenzí na Googlu",
      share: "podíl pětihvězdičkových recenzí v procentech",
      date: "datum souhrnu Googlu",
    },
    googleSummaryTitle: "Souhrn Googlu",
    googleSummarySource: "Zdroj",
    googleSummarySourceExport: "Export Takeout ({{name}})",
    googleSummarySourceManual: "Ruční čísla ze záložky Společnost",
    googleSummaryScore: "Známka",
    googleSummaryCount: "Počet",
    googleSummaryDate: "Datum",
    googleSummaryNoExport: "Do prvního nahraného exportu platí ruční čísla ze záložky Společnost.",
    googleSummaryWithExport:
      "Souhrn se počítá z posledního potvrzeného exportu, ruční čísla zůstávají jako záloha.",
    googleSummaryLoadError: "Souhrn Googlu se nepodařilo načíst.",
  },
};
